/**
 *
 * @module accessStatus
 *
 */

import Check from 'check-types';
import Services from './../services/entry';
import RedeemRetryHandler from './redeemRetryHandler';

const PurchaseActivationResult = Services.Purchase.PurchaseActivationResult;
const RetryPolicy = Services.Configuration.RetryPolicy;

const ErrorReason = Services.Exception.ErrorReason;
const ExceptionReference = Services.Exception.ExceptionReference;
const ServiceException = Services.Exception.ServiceException;
const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict
} = Check.assert;

/**
 *
 * @class AccessStatus
 *
 */
export default class AccessStatus {

    /**
     *
     * @param {SDK.Services.Purchase.PurchaseActivationResult} purchaseActivationResult
     * @param {SDK.Services.Configuration.RetryPolicy} policy
     * @param {SDK.Purchase.RedeemRetryHandler} [handler=null]
     *
     */
    constructor(purchaseActivationResult, policy, handler = null) {

        const errorMessage = {
            ctor: 'AccessStatus(purchaseActivationResult)',
            get purchaseActivationResult() {
                return `${this.ctor} needs SDK.Services.Purchase.PurchaseActivationResult instance`;
            },
            get policy() {
                return `${this.ctor} needs SDK.Services.Configuration.RetryPolicy instance`;
            },
            get handler() {
                return `${this.ctor} needs SDK.Purchase.RedeemRetryHandler instance`;
            }
        };

        instanceStrict(purchaseActivationResult, PurchaseActivationResult, errorMessage.purchaseActivationResult, InvalidArgumentException); // eslint-disable-line
        instanceStrict(policy, RetryPolicy, errorMessage.policy, InvalidArgumentException);

        if (Check.assigned(handler)) {
            instanceStrict(handler, RedeemRetryHandler, errorMessage.handler, InvalidArgumentException);
        }

        const { retryBasePeriod, retryMaxPeriod, retryMaxAttempts, retryMultiplier } = policy;

        /**
         *
         * @access public
         * @type {Boolean}
         * @desc does a user have temporary access to some content
         * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/purchase.md#http-202
         *
         */
        this.isTemporary = purchaseActivationResult.temporaryAccessGranted || false;

        /**
         *
         * @access public
         * @type {Array<SDK.Services.Purchase.PurchaseActivation>}
         * @desc Purchase information for all activated SKUs.
         *
         */
        this.purchases = purchaseActivationResult.purchases;

        /**
         *
         * @access private
         * @type {SDK.Purchase.RedeemRetryHandler}
         * @desc handler used to retry redeem
         *
         */
        this.handler = handler;

        /**
         *
         * @access private
         * @type {Number}
         * @desc time between when to retry redeem in milliseconds
         *
         */
        this.interval = retryBasePeriod * 1000;

        /**
         *
         * @access private
         * @type {Number}
         * @desc max time between when to retry in milliseconds
         *
         */
        this.retryMaxPeriod = retryMaxPeriod * 1000;

        /**
         *
         * @access private
         * @type {Number}
         * @desc maximum number of attemps to retry
         *
         */
        this.retryMaxAttempts = retryMaxAttempts;

        /**
         *
         * @access private
         * @type {Number}
         * @desc amount to increase the interval
         *
         */
        this.retryMultiplier = retryMultiplier;

        /**
         *
         * @access private
         * @type {Number}
         * @desc number of attempts to retry redeem of purchase
         *
         */
        this.attempts = 0;

    }

    /**
     *
     * @access public
     * @desc attempts to make purchase permanant
     * @returns {Promise<AccessStatus>}
     *
     */
    makePermanent() {

        const { interval, retryMaxPeriod } = this;
        const hasRetryMaxPeriod = Check.assigned(retryMaxPeriod) && Check.not.zero(retryMaxPeriod);

        let retryDelay;

        if (hasRetryMaxPeriod && interval > retryMaxPeriod) {
            retryDelay = retryMaxPeriod;
        } else {
            retryDelay = interval;
        }

        return new Promise((resolve) => setTimeout(resolve, retryDelay)).then(() => {

            return this.retryRedeem();

        });

    }

    /**
     *
     * @access private
     * @desc retries to redeem purchase using the handler
     * @returns {Promise<AccessStatus>}
     *
     */
    retryRedeem() {

        const { handler, retryMaxAttempts, retryMultiplier } = this;

        if (Check.not.assigned(handler)) {

            const reasons = [new ErrorReason('', 'Invalid: For use with Redeem')];
            const exceptionData = ExceptionReference.common.invalidState;

            return Promise.reject(new ServiceException({ reasons, exceptionData }));

        }

        return handler.run().then((result) => {

            this.attempts++;

            if (Check.nonEmptyArray(result.errors)) {
                return Promise.reject(result.errors);
            }

            if (result.isTemporary) {

                if (this.attempts >= retryMaxAttempts) {

                    const reasons = [new ErrorReason('', 'Max Retries Reached')];
                    const exceptionData = ExceptionReference.common.invalidState;

                    return Promise.reject(new ServiceException({ reasons, exceptionData }));

                } else {

                    this.interval = this.interval * retryMultiplier;

                    return this.makePermanent();

                }

            }

            return Promise.resolve(result);

        });

    }

}



// WEBPACK FOOTER //
// ./src/purchase/accessStatus.js